import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import '../styles/modern-design-system.css';
import {
  TrendingUp, Users, AlertCircle, CheckCircle,
  ArrowUp, ArrowDown, MoreVertical, Search,
  Filter, Download, RefreshCw, Calendar,
  Clock, Activity, DollarSign, Target,
  Award, BarChart3, PieChart, Shield,
  Menu, X, Bell, Settings, LogOut,
  ChevronRight, Eye, Edit, Trash2,
  FileText, Building2, UserCheck
} from 'lucide-react';

const ModernDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // KPI Data
  const [kpiData] = useState({
    totalComplaints: 1247,
    complaintsTrend: 12.5,
    activeInvestigations: 89,
    investigationsTrend: -3.2,
    resolvedCases: 1158,
    resolvedTrend: 18.7,
    avgResolutionTime: 4.2,
    resolutionTrend: -15.3,
    userSatisfaction: 94.2,
    satisfactionTrend: 2.1,
    complianceRate: 98.5,
    complianceTrend: 0.8
  });

  // Navigation Items
  const navigationItems = [
    { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
    { id: 'investigations', label: 'Investigações', icon: Search },
    { id: 'complaints', label: 'Denúncias', icon: AlertCircle },
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'forms', label: 'Formulários', icon: FileText },
    { id: 'companies', label: 'Empresas', icon: Building2 },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'settings', label: 'Configurações', icon: Settings }
  ];

  const handleNavigation = (viewId) => {
    if (viewId === 'investigations') {
      navigate('/investigation');
    } else {
      setActiveView(viewId);
    }
    setSidebarOpen(false);
  };

  const refreshData = async () => {
    setRefreshing(true);
    // Simulate data refresh
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRefreshing(false);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)' }}>
      {/* Modern Navbar */}
      <nav className="navbar-modern">
        <div className="navbar-content">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="btn-ghost-modern btn-sm hide-lg"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            
            <div className="navbar-brand">
              <Shield style={{ color: 'var(--primary)' }} />
              <span>REPORTFius</span>
            </div>
          </div>

          {/* Center Section - Desktop Navigation */}
          <div className="navbar-menu hide-md">
            {navigationItems.slice(0, 5).map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`navbar-item ${activeView === item.id ? 'active' : ''}`}
                >
                  <Icon size={18} />
                  <span style={{ marginLeft: '8px' }}>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            <button className="btn-ghost-modern btn-sm" onClick={refreshData}>
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
            
            <button className="btn-ghost-modern btn-sm position-relative">
              <Bell size={18} />
              <span style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '8px',
                height: '8px',
                background: 'var(--danger)',
                borderRadius: '50%'
              }}></span>
            </button>

            <div className="flex items-center gap-2 hide-sm">
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-full)',
                background: 'var(--primary-gradient)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600'
              }}>
                {user?.full_name?.charAt(0) || 'A'}
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold">{user?.full_name || 'Admin'}</div>
                <div className="text-xs" style={{ color: 'var(--gray-600)' }}>
                  {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                </div>
              </div>
            </div>

            <button 
              onClick={() => navigate('/simple')}
              className="btn-primary-modern btn-sm"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                marginRight: '8px'
              }}
            >
              🚀 Dashboard Simples
            </button>
            <button onClick={logout} className="btn-ghost-modern btn-sm">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      <div className={`sidebar-modern ${sidebarOpen ? 'mobile-open' : 'mobile-hidden'} show-lg`}>
        <div className="sidebar-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield size={28} style={{ color: 'var(--primary)' }} />
              <span className="font-bold text-lg">REPORTFius</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="btn-ghost-modern btn-sm">
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="sidebar-menu">
          {navigationItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.id)}
                className={`sidebar-item ${activeView === item.id ? 'active' : ''}`}
              >
                <Icon className="sidebar-item-icon" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 'var(--space-4)' }}>
          <button onClick={logout} className="btn-modern btn-gradient btn-md w-full">
            <LogOut size={18} />
            <span>Sair do Sistema</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-modern" style={{ paddingTop: 'var(--space-8)', paddingBottom: 'var(--space-8)' }}>
        {/* Page Header */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <h1 className="text-3xl font-extrabold" style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-2)' }}>
            {navigationItems.find(item => item.id === activeView)?.label || 'Dashboard'}
          </h1>
          <p style={{ color: 'var(--gray-600)' }}>
            Bem-vindo de volta, {user?.full_name || 'Administrador'}
          </p>
        </div>

        {/* KPI Cards Grid */}
        {activeView === 'overview' && (
          <>
            <div className="grid-modern grid-cols-4 lg:grid-cols-2 md:grid-cols-1" style={{ marginBottom: 'var(--space-8)' }}>
              {/* Total Complaints KPI */}
              <div className="kpi-card">
                <div className="kpi-card-icon" style={{ background: 'rgba(91, 108, 255, 0.1)' }}>
                  <AlertCircle style={{ color: 'var(--primary)' }} />
                </div>
                <div className="kpi-card-value">{kpiData.totalComplaints.toLocaleString()}</div>
                <div className="kpi-card-label">Total de Denúncias</div>
                <div className={`kpi-card-trend ${kpiData.complaintsTrend > 0 ? 'up' : 'down'}`}>
                  {kpiData.complaintsTrend > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                  {Math.abs(kpiData.complaintsTrend)}% este mês
                </div>
              </div>

              {/* Active Investigations KPI */}
              <div className="kpi-card">
                <div className="kpi-card-icon" style={{ background: 'rgba(255, 184, 0, 0.1)' }}>
                  <Search style={{ color: 'var(--warning)' }} />
                </div>
                <div className="kpi-card-value">{kpiData.activeInvestigations}</div>
                <div className="kpi-card-label">Investigações Ativas</div>
                <div className={`kpi-card-trend ${kpiData.investigationsTrend > 0 ? 'up' : 'down'}`}>
                  {kpiData.investigationsTrend > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                  {Math.abs(kpiData.investigationsTrend)}% variação
                </div>
              </div>

              {/* Resolved Cases KPI */}
              <div className="kpi-card">
                <div className="kpi-card-icon" style={{ background: 'rgba(46, 213, 115, 0.1)' }}>
                  <CheckCircle style={{ color: 'var(--success)' }} />
                </div>
                <div className="kpi-card-value">{kpiData.resolvedCases.toLocaleString()}</div>
                <div className="kpi-card-label">Casos Resolvidos</div>
                <div className={`kpi-card-trend ${kpiData.resolvedTrend > 0 ? 'up' : 'down'}`}>
                  {kpiData.resolvedTrend > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                  {Math.abs(kpiData.resolvedTrend)}% crescimento
                </div>
              </div>

              {/* Average Resolution Time KPI */}
              <div className="kpi-card">
                <div className="kpi-card-icon" style={{ background: 'rgba(0, 210, 211, 0.1)' }}>
                  <Clock style={{ color: 'var(--info)' }} />
                </div>
                <div className="kpi-card-value">{kpiData.avgResolutionTime} dias</div>
                <div className="kpi-card-label">Tempo Médio de Resolução</div>
                <div className={`kpi-card-trend ${kpiData.resolutionTrend < 0 ? 'up' : 'down'}`}>
                  {kpiData.resolutionTrend < 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                  {Math.abs(kpiData.resolutionTrend)}% mais rápido
                </div>
              </div>
            </div>

            {/* Secondary KPI Row */}
            <div className="grid-modern grid-cols-2 md:grid-cols-1" style={{ marginBottom: 'var(--space-8)' }}>
              {/* User Satisfaction */}
              <div className="card-modern">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>
                      Satisfação dos Usuários
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-extrabold">{kpiData.userSatisfaction}%</span>
                      <div className={`kpi-card-trend ${kpiData.satisfactionTrend > 0 ? 'up' : 'down'}`}>
                        {kpiData.satisfactionTrend > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                        {Math.abs(kpiData.satisfactionTrend)}%
                      </div>
                    </div>
                  </div>
                  <Award size={48} style={{ color: 'var(--warning)', opacity: 0.2 }} />
                </div>
              </div>

              {/* Compliance Rate */}
              <div className="card-modern">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>
                      Taxa de Compliance
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-extrabold">{kpiData.complianceRate}%</span>
                      <div className={`kpi-card-trend ${kpiData.complianceTrend > 0 ? 'up' : 'down'}`}>
                        {kpiData.complianceTrend > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                        {Math.abs(kpiData.complianceTrend)}%
                      </div>
                    </div>
                  </div>
                  <Shield size={48} style={{ color: 'var(--success)', opacity: 0.2 }} />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card-modern" style={{ marginBottom: 'var(--space-8)' }}>
              <h2 className="card-title-modern" style={{ marginBottom: 'var(--space-6)' }}>Ações Rápidas</h2>
              <div className="grid-modern grid-cols-4 lg:grid-cols-2 md:grid-cols-1">
                <button 
                  onClick={() => navigate('/investigation')}
                  className="btn-modern btn-gradient btn-lg w-full"
                  style={{ flexDirection: 'column', height: 'auto', padding: 'var(--space-6)' }}
                >
                  <Search size={32} style={{ marginBottom: 'var(--space-2)' }} />
                  <span>Nova Investigação</span>
                </button>
                
                <button 
                  onClick={() => setActiveView('complaints')}
                  className="btn-modern btn-outline-modern btn-lg w-full"
                  style={{ flexDirection: 'column', height: 'auto', padding: 'var(--space-6)' }}
                >
                  <AlertCircle size={32} style={{ marginBottom: 'var(--space-2)' }} />
                  <span>Ver Denúncias</span>
                </button>
                
                <button 
                  onClick={() => setActiveView('users')}
                  className="btn-modern btn-outline-modern btn-lg w-full"
                  style={{ flexDirection: 'column', height: 'auto', padding: 'var(--space-6)' }}
                >
                  <Users size={32} style={{ marginBottom: 'var(--space-2)' }} />
                  <span>Gerenciar Usuários</span>
                </button>
                
                <button 
                  onClick={() => setActiveView('analytics')}
                  className="btn-modern btn-outline-modern btn-lg w-full"
                  style={{ flexDirection: 'column', height: 'auto', padding: 'var(--space-6)' }}
                >
                  <BarChart3 size={32} style={{ marginBottom: 'var(--space-2)' }} />
                  <span>Ver Analytics</span>
                </button>
              </div>
            </div>

            {/* Recent Activity Table */}
            <div className="card-modern">
              <div className="card-header-modern">
                <h2 className="card-title-modern">Atividade Recente</h2>
                <button className="btn-ghost-modern btn-sm">
                  <MoreVertical size={18} />
                </button>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Status</th>
                      <th>Data</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>#1247</td>
                      <td>
                        <span className="badge-modern badge-primary">Denúncia</span>
                      </td>
                      <td>Suspeita de fraude em licitação</td>
                      <td>
                        <span className="badge-modern badge-warning">Em Análise</span>
                      </td>
                      <td>Hoje, 14:23</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn-ghost-modern btn-sm">
                            <Eye size={16} />
                          </button>
                          <button className="btn-ghost-modern btn-sm">
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>#1246</td>
                      <td>
                        <span className="badge-modern badge-success">Investigação</span>
                      </td>
                      <td>Verificação de compliance concluída</td>
                      <td>
                        <span className="badge-modern badge-success">Resolvido</span>
                      </td>
                      <td>Hoje, 11:45</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn-ghost-modern btn-sm">
                            <Eye size={16} />
                          </button>
                          <button className="btn-ghost-modern btn-sm">
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>#1245</td>
                      <td>
                        <span className="badge-modern badge-danger">Alerta</span>
                      </td>
                      <td>Nova denúncia urgente recebida</td>
                      <td>
                        <span className="badge-modern badge-danger">Pendente</span>
                      </td>
                      <td>Ontem, 16:30</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn-ghost-modern btn-sm">
                            <Eye size={16} />
                          </button>
                          <button className="btn-ghost-modern btn-sm">
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Other Views Placeholder */}
        {activeView !== 'overview' && (
          <div className="card-modern" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="text-center">
              <div className="spinner-modern" style={{ margin: '0 auto var(--space-4)' }}></div>
              <p style={{ color: 'var(--gray-600)' }}>Carregando {navigationItems.find(item => item.id === activeView)?.label}...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernDashboard;